/**
 * SanctaMemoryDrawer — Slice 4 UI.
 *
 * A right-side sheet that lists everything Sancta remembers about the
 * current user. Users can pin, forget, or manually add memories.
 *
 * Backed by `/api/sancta/memory` (auth required).
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrainCircuit, Pin, PinOff, Trash2, Plus, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

interface Memory {
  memory_id: string;
  category: "preference" | "fact" | "follow_up";
  text: string;
  pinned: boolean;
  created_at?: string;
}

const CATEGORY_META: Record<string, { label: string; color: string }> = {
  preference: { label: "Preference", color: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300" },
  fact:       { label: "Fact",       color: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-300" },
  follow_up:  { label: "Follow-up",  color: "bg-amber-500/10 text-amber-600 dark:text-amber-300" },
};

export const SanctaMemoryDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const { user } = useAuth();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newCat, setNewCat] = useState<Memory["category"]>("preference");

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/sancta/memory");
      setMemories(data.memories || []);
    } catch (e: any) {
      toast.error("Could not load memory ledger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open, user?.user_id]);

  // Close on Escape key press — accessibility polish (iter-133 finding).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const groups = useMemo(() => {
    const g: Record<string, Memory[]> = { preference: [], fact: [], follow_up: [] };
    memories.forEach(m => { (g[m.category] ||= []).push(m); });
    return g;
  }, [memories]);

  const pin = async (m: Memory) => {
    try {
      await api.patch(`/sancta/memory/${m.memory_id}/pin`, { pinned: !m.pinned });
      setMemories(mm => mm.map(x => x.memory_id === m.memory_id ? { ...x, pinned: !m.pinned } : x));
    } catch { toast.error("Could not update pin"); }
  };
  const forget = async (m: Memory) => {
    try {
      await api.delete(`/sancta/memory/${m.memory_id}`);
      setMemories(mm => mm.filter(x => x.memory_id !== m.memory_id));
      toast.success("Forgotten.");
    } catch { toast.error("Could not forget memory"); }
  };
  const clearAll = async () => {
    if (!confirm("Forget every note Sancta has kept about you? This cannot be undone.")) return;
    try {
      await api.delete("/sancta/memory");
      setMemories([]);
      toast.success("Memory ledger cleared.");
    } catch { toast.error("Could not clear memory"); }
  };
  const addManual = async () => {
    const text = newText.trim();
    if (text.length < 3) return;
    try {
      const { data } = await api.post("/sancta/memory", { text, category: newCat });
      setMemories(mm => [data.memory, ...mm]);
      setNewText("");
      setAdding(false);
      toast.success("Saved to memory ledger.");
    } catch { toast.error("Could not save memory"); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 z-[100]"
            data-testid="sancta-memory-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="fixed right-0 top-0 bottom-0 z-[101] w-full sm:w-[420px] bg-white dark:bg-slate-950 border-l border-neutral-200 dark:border-white/10 shadow-2xl flex flex-col"
            data-testid="sancta-memory-drawer"
          >
            <header className="p-4 border-b border-neutral-200 dark:border-white/10 flex items-center gap-2">
              <BrainCircuit className="h-5 w-5 text-fuchsia-500" />
              <div className="flex-1">
                <div className="font-semibold text-sm">Sancta Memory</div>
                <div className="text-[11px] text-neutral-500 dark:text-slate-400">What Sancta remembers about you</div>
              </div>
              <button
                onClick={onClose}
                data-testid="sancta-memory-close"
                className="h-8 w-8 rounded-full grid place-items-center hover:bg-neutral-100 dark:hover:bg-white/5"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            {!user && (
              <div className="p-6 text-center text-sm text-neutral-500 dark:text-slate-400">
                <ShieldCheck className="mx-auto mb-2 h-6 w-6" />
                Sign in to keep a personal memory ledger.
              </div>
            )}

            {user && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="rounded-lg border border-dashed border-neutral-300 dark:border-white/15 p-3">
                  {!adding ? (
                    <button
                      onClick={() => setAdding(true)}
                      data-testid="sancta-memory-add-open"
                      className="inline-flex items-center gap-2 text-xs font-medium text-fuchsia-600 dark:text-fuchsia-300 hover:underline"
                    >
                      <Plus className="h-3.5 w-3.5" /> Teach Sancta something new
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        value={newText}
                        onChange={e => setNewText(e.target.value)}
                        placeholder="e.g. I only buy cruelty-free skincare"
                        maxLength={280}
                        rows={3}
                        data-testid="sancta-memory-add-text"
                        className="w-full text-sm p-2 rounded border border-neutral-200 dark:border-white/10 bg-white dark:bg-slate-900"
                      />
                      <div className="flex items-center gap-2">
                        <select
                          value={newCat}
                          onChange={e => setNewCat(e.target.value as any)}
                          data-testid="sancta-memory-add-category"
                          className="text-xs p-1.5 rounded border border-neutral-200 dark:border-white/10 bg-white dark:bg-slate-900"
                        >
                          <option value="preference">Preference</option>
                          <option value="fact">Fact</option>
                          <option value="follow_up">Follow-up</option>
                        </select>
                        <button
                          onClick={addManual}
                          disabled={newText.trim().length < 3}
                          data-testid="sancta-memory-add-save"
                          className="ml-auto text-xs px-3 py-1.5 rounded bg-fuchsia-600 text-white disabled:opacity-40"
                        >Save</button>
                        <button
                          onClick={() => { setAdding(false); setNewText(""); }}
                          className="text-xs px-2 py-1.5 rounded text-neutral-500 hover:text-neutral-800 dark:hover:text-white"
                        >Cancel</button>
                      </div>
                    </div>
                  )}
                </div>

                {loading && <div className="text-xs text-neutral-500 dark:text-slate-400">Loading…</div>}

                {!loading && memories.length === 0 && (
                  <div className="text-center text-xs text-neutral-500 dark:text-slate-400 py-8">
                    Sancta will build this ledger automatically as you chat.
                    <br />You can also pin things manually above.
                  </div>
                )}

                {(["preference", "fact", "follow_up"] as const).map(cat => {
                  const list = groups[cat] || [];
                  if (!list.length) return null;
                  const meta = CATEGORY_META[cat];
                  return (
                    <section key={cat} data-testid={`sancta-memory-group-${cat}`}>
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-400 mb-2">
                        {meta.label} ({list.length})
                      </div>
                      <ul className="space-y-2">
                        {list.map(m => (
                          <li
                            key={m.memory_id}
                            data-testid={`sancta-memory-row-${m.memory_id}`}
                            className="group rounded-lg border border-neutral-200 dark:border-white/10 p-2.5 pr-2 flex items-start gap-2"
                          >
                            <span className={`shrink-0 mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${meta.color}`}>
                              {meta.label}
                            </span>
                            <div className="flex-1 text-sm leading-snug">{m.text}</div>
                            <button
                              onClick={() => pin(m)}
                              data-testid={`sancta-memory-pin-${m.memory_id}`}
                              className={`shrink-0 h-7 w-7 rounded grid place-items-center ${m.pinned ? "text-fuchsia-500" : "text-neutral-400 hover:text-neutral-700 dark:hover:text-white"}`}
                              title={m.pinned ? "Unpin" : "Pin"}
                            >
                              {m.pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              onClick={() => forget(m)}
                              data-testid={`sancta-memory-forget-${m.memory_id}`}
                              className="shrink-0 h-7 w-7 rounded grid place-items-center text-neutral-400 hover:text-red-500"
                              title="Forget"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  );
                })}
              </div>
            )}

            {user && memories.length > 0 && (
              <footer className="p-3 border-t border-neutral-200 dark:border-white/10 flex items-center justify-between">
                <span className="text-[11px] text-neutral-500 dark:text-slate-400">
                  {memories.length} note{memories.length !== 1 ? "s" : ""}
                </span>
                <button
                  onClick={clearAll}
                  data-testid="sancta-memory-clear-all"
                  className="text-[11px] text-red-500 hover:underline"
                >Forget everything</button>
              </footer>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default SanctaMemoryDrawer;
